import { format } from "date-fns";
import moment from "moment";
import React, { forwardRef, useEffect, useState } from "react";
import ReactDatePicker, {
  CalendarContainer,
  CalendarContainerProps,
} from "react-datepicker";
import { CloseIcon } from "src/assets/icons";
import { listHoliday } from "src/helper/consts";
import {
  getMonthDateString,
  getYearMonthString,
  registerDatepickerLocale,
} from "src/utils/datetime";
import { listWeekShort } from "../../utils/list-week-short";
import "./index.scss";
registerDatepickerLocale();

interface IProps {
  initValue?: string | null;
  IconDate?: React.FC;
  onChange?: (d?: Date) => void;
  placeholder?: string;
  allowSelectPastDate?: boolean;
  remove?: boolean;
  maxDate?: Date | null;
}

function NafutelDatePickerComponent({
  initValue = null,
  IconDate,
  onChange,
  placeholder = "",
  allowSelectPastDate = false,
  remove = false,
  ...rest
}: IProps) {
  const [date, setDate] = useState<Date | null | undefined>(
    initValue ? moment(initValue, "YYYYMMDD").toDate() : null
  );
  const twoYearFromNow = new Date();
  twoYearFromNow.setFullYear(twoYearFromNow.getFullYear() + 2);

  const handleRemoveValue = () => {
    setDate(undefined);
    onChange && onChange(undefined);
  };

  const CustomInput = forwardRef(({ value, onClick }: any, ref: any) => (
    <button className="nafutel-date-input" onClick={onClick} ref={ref}>
      <span className={`txt-value ${!value ? "txt-place-holder" : ""}`}>
        {value ? formatDateChoosed(value) : placeholder}
        {value && remove && (
          <CloseIcon
            className="ms-4 py-1"
            width={20}
            height={18}
            onClick={handleRemoveValue}
          />
        )}
      </span>
      {IconDate ? <IconDate /> : null}
    </button>
  ));

  useEffect(() => {
    setDate(initValue ? moment(initValue, "YYYYMMDD").toDate() : null);
  }, [initValue]);

  const formatDateChoosed = (date: Date) => {
    if (!date) return "";
    const date1 = getMonthDateString(date, "MM/DD/YYYY");
    return `${date1} (${listWeekShort()[moment(date).weekday()]})`;
  };

  const MyContainer = ({ className, children }: CalendarContainerProps) => {
    return (
      <CalendarContainer className={className}>
        <div style={{ position: "relative" }}>{children}</div>
      </CalendarContainer>
    );
  };

  const customDayClassName = (date: Date) => {
    const day = date.getDay();
    const dateAvailable = date > new Date() && date < twoYearFromNow;
    return dateAvailable
      ? day === 0
        ? "sunday"
        : day === 6
        ? "saturday"
        : listHoliday.includes(format(date, "dd-MM"))
        ? "holiday"
        : ""
      : "";
  };

  return (
    <div className="DatePickerStyle">
      <ReactDatePicker
        selected={date}
        minDate={allowSelectPastDate ? null : new Date()}
        maxDate={twoYearFromNow}
        locale={localStorage.getItem("lang") || "ja"}
        showPopperArrow={false}
        onChange={(d: Date) => {
          setDate(d);
          if (onChange) onChange(d);
        }}
        customInput={<CustomInput />}
        calendarContainer={MyContainer}
        calendarClassName="nafutel-canlendar"
        dayClassName={customDayClassName}
        renderCustomHeader={({
          monthDate,
          customHeaderCount,
          decreaseMonth,
          increaseMonth,
          nextMonthButtonDisabled,
        }) => {
          const currentMonth = new Date().getMonth() + 1;
          const currentYear = new Date().getFullYear();
          const currentMonthPrevious = monthDate.getMonth() + 1;
          const currentYearMonthDate = monthDate.getFullYear();

          return (
            <div className="wrap-header-control">
              <button
                className="btn-control"
                style={{
                  cursor: "pointer",
                  visibility: customHeaderCount === 1 ? "hidden" : "visible",
                  pointerEvents:
                    currentMonthPrevious === currentMonth &&
                    currentYear === currentYearMonthDate &&
                    !allowSelectPastDate
                      ? "none"
                      : "all",
                }}
                onClick={decreaseMonth}
              >
                <svg
                  width="9"
                  height="14"
                  viewBox="0 0 9 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8.40007 2.40007L7 0L0.000349974 6.99965L1.40042 8.39972L8.40007 2.40007Z"
                    fill={`${
                      currentMonthPrevious === currentMonth &&
                      currentYear === currentYearMonthDate &&
                      !allowSelectPastDate
                        ? "#BFBFBF"
                        : "#3F9BC0"
                    }`}
                  />
                  <path
                    d="M1.40007 5.5999L0 6.99997L6.99965 13.9996L8.39972 12.5995L1.40007 5.5999Z"
                    fill={`${
                      currentMonthPrevious === currentMonth &&
                      currentYear === currentYearMonthDate &&
                      !allowSelectPastDate
                        ? "#BFBFBF"
                        : "#3F9BC0"
                    }`}
                  />
                </svg>
              </button>
              <span className="react-datepicker__current-month">
                <div className="nafutel-year">
                  {getYearMonthString(monthDate)}
                </div>
              </span>
              <button
                className="btn-control"
                style={{
                  cursor: "pointer",
                  visibility: nextMonthButtonDisabled ? "hidden" : "visible",
                  pointerEvents: nextMonthButtonDisabled ? "none" : "all",
                }}
                onClick={increaseMonth}
              >
                <svg
                  width="9"
                  height="14"
                  viewBox="0 0 9 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M6.99983 8.40007L8.3999 7L1.39955 -0.000356744L-0.000525817 2.39971L6.99983 8.40007Z"
                    fill={`${nextMonthButtonDisabled ? "#BFBFBF" : "#3F9BC0"}`}
                  />
                  <path
                    d="M-0.000169073 12.5999L1.3999 14L8.40026 6.99964L7.00019 5.59957L-0.000169073 12.5999Z"
                    fill={`${nextMonthButtonDisabled ? "#BFBFBF" : "#3F9BC0"}`}
                  />
                </svg>
              </button>
            </div>
          );
        }}
        {...rest}
      />
    </div>
  );
}

export default NafutelDatePickerComponent;
